import { z } from 'zod'

/**
 * Localized string schema for multi-language support.
 * Keys are locale codes (e.g., 'en', 'de'), values are the translated text.
 */
export const LocalizedStringSchema = z.record(z.string(), z.string())

export type LocalizedString = z.infer<typeof LocalizedStringSchema>

/**
 * Operation data schema - Simple and extendable base schema.
 * 
 * This base schema is minimal - organizations can extend it by defining
 * custom field schemas via the admin API (stored in EnvironmentSetting).
 * 
 * The UI forms are dynamically generated from the schema definition,
 * so custom fields can be added without code changes.
 */
export const OperationDataSchema = z.record(z.string(), z.unknown())

export type OperationData = z.infer<typeof OperationDataSchema>

/**
 * Field definition schema for dynamic form generation.
 * These field definitions can be stored in EnvironmentSetting and
 * used to generate forms dynamically using TanStack Form.
 */
export const FieldDefinitionSchema = z.object({
  name: z.string().describe('Field name (key in the data object)'),
  label: z.union([z.string(), LocalizedStringSchema]).describe('Display label (string or localized)'),
  shortName: z.string().optional().describe('Optional short label for compact UIs and exports'),
  unit: z.string().optional().describe('Optional unit suffix, e.g. mm, rpm, °C'),
  type: z.enum(['text', 'number', 'select', 'date', 'textarea', 'checkbox']).describe('Input type'),
  required: z.boolean().optional().default(false),
  description: z.union([z.string(), LocalizedStringSchema]).optional().describe('Help text for the field'),
  placeholder: z.string().optional(),
  options: z.array(z.object({
    value: z.string(),
    label: z.union([z.string(), LocalizedStringSchema]),
  })).optional().describe('Options for select fields'),
  min: z.number().optional().describe('Minimum value for number fields'),
  max: z.number().optional().describe('Maximum value for number fields'),
  step: z.number().optional().describe('Step value for number fields'),
  group: z.string().optional().describe('Group name for organizing fields'),
  visibility: z.enum(['basic', 'expert']).optional().describe('Visibility level for progressive forms'),
  defaultValue: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional().describe('Default value for the field'),
})

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>

/**
 * Form schema definition - Defines a complete form structure.
 * This is stored in EnvironmentSetting and used to generate forms.
 */
export const FormSchemaDefinitionSchema = z.object({
  id: z.string().describe('Unique identifier for the form schema'),
  name: z.string().describe('Display name for the form'),
  description: z.string().optional(),
  fields: z.array(FieldDefinitionSchema),
  groups: z.array(z.object({
    name: z.string(),
    label: z.union([z.string(), LocalizedStringSchema]),
    sectionColumns: z.number().int().min(1).max(4).optional().default(1),
    collapsible: z.boolean().optional().default(true),
    defaultOpen: z.boolean().optional().default(false),
  })).optional().describe('Field groupings for UI organization'),
  layout: z.enum(['single-col', 'multi-col']).optional().default('multi-col'),
})

export type FormSchemaDefinition = z.infer<typeof FormSchemaDefinitionSchema>

/**
 * Schema history entry for tracking schema versions.
 */
export const SchemaHistoryEntrySchema = z.object({
  version: z.number(),
  formSchema: FormSchemaDefinitionSchema,
  migratedAt: z.string(), // ISO date string
})

export type SchemaHistoryEntry = z.infer<typeof SchemaHistoryEntrySchema>

/**
 * Operation type schema for the full OperationType model.
 */
export const OperationTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  labels: LocalizedStringSchema,
  description: LocalizedStringSchema.optional().nullable(),
  icon: z.string().optional().nullable(),
  version: z.number().default(1),
  formSchema: FormSchemaDefinitionSchema,
  schemaHistory: z.array(SchemaHistoryEntrySchema).optional().nullable(),
  organizationId: z.string().optional().nullable(),
  clonedFromId: z.string().optional().nullable(),
  enabled: z.boolean().default(true),
  createdAt: z.string(), // ISO date
  updatedAt: z.string(), // ISO date
})

export type OperationType = z.infer<typeof OperationTypeSchema>

/**
 * Schema migration field mapping for version upgrades.
 */
export const SchemaMigrationFieldMappingSchema = z.object({
  oldFieldName: z.string(),
  newFieldName: z.string().optional(),
  transform: z.enum(['copy', 'delete', 'default']).default('copy'),
  defaultValue: z.unknown().optional(),
})

export type SchemaMigrationFieldMapping = z.infer<typeof SchemaMigrationFieldMappingSchema>

/**
 * Schema migration definition for upgrading operations between schema versions.
 */
export const SchemaMigrationSchema = z.object({
  fromVersion: z.number(),
  toVersion: z.number(),
  fieldMappings: z.array(SchemaMigrationFieldMappingSchema),
})

export type SchemaMigration = z.infer<typeof SchemaMigrationSchema>

/**
 * Default operation form fields - Simple set that can be extended by admin.
 * These represent the minimum fields a service technician would record.
 */
export const defaultOperationFields: FieldDefinition[] = [
  { name: 'operationType', label: 'Operation Type', type: 'text', required: true },
  { name: 'notes', label: 'Notes', type: 'textarea', required: false },
]
